'use client'

import { useEffect, useState } from 'react'
import {
  Lock,
  Zap,
  BarChart3,
  Bot,
  Sparkles,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react'
import Link from 'next/link'

interface UsageLimitGateProps {
  toolName: string
  used: number
  limit: number
}

interface SessionStatus {
  signedIn: boolean
  creditsBalance?: number
  subscriptionActive?: boolean
}

/**
 * Full-screen gate shown when a user has exhausted free tool runs.
 *
 * Behavior splits on session state:
 *   - Anonymous OR signed-in with no paid access → original "Get Radar" upsell
 *   - Signed-in WITH credits/subscription → "Open dashboard" — they already
 *     paid, never show "buy another block" to a paying customer (Roy incident).
 */
export function UsageLimitGate({ limit }: UsageLimitGateProps) {
  const [session, setSession] = useState<SessionStatus | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch('/api/platform/session/status')
      .then(r => (r.ok ? r.json() : { signedIn: false }))
      .then(data => {
        if (!cancelled) setSession(data)
      })
      .catch(() => {
        if (!cancelled) setSession({ signedIn: false })
      })
    return () => {
      cancelled = true
    }
  }, [])

  const hasPaidAccess =
    session?.signedIn &&
    ((session.creditsBalance ?? 0) > 0 || session.subscriptionActive === true)

  if (hasPaidAccess) {
    return <PaidUserGate balance={session!.creditsBalance ?? 0} />
  }

  return <UpsellGate limit={limit} />
}

function PaidUserGate({ balance }: { balance: number }) {
  return (
    <div className='bg-muted/50 rounded-2xl border border-line-strong p-8 md:p-12 text-center'>
      <div className='max-w-lg mx-auto space-y-6'>
        <div
          className='w-16 h-16 rounded-2xl flex items-center justify-center mx-auto'
          style={{ backgroundColor: '#008dcc15' }}
        >
          <CheckCircle2 size={28} style={{ color: '#008dcc' }} />
        </div>

        <div className='space-y-2'>
          <h3 className='text-xl font-semibold text-foreground'>
            You&apos;ve hit the daily limit on this free tool
          </h3>
          <p className='text-muted-foreground text-sm'>
            {balance > 0 ? (
              <>
                Good news — you have{' '}
                <strong className='text-foreground'>
                  {balance} audit credit{balance === 1 ? '' : 's'}
                </strong>{' '}
                in your dashboard. Run unlimited audits there with all 13 tools
                in one report.
              </>
            ) : (
              <>
                Your subscription gives you unlimited audits in the dashboard
                with all 13 tools in one report.
              </>
            )}
          </p>
        </div>

        <div className='flex flex-col sm:flex-row gap-3 justify-center pt-2'>
          <Link
            href='/platform/app'
            className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-sm transition-colors'
            style={{ backgroundColor: '#008dcc', color: '#fff' }}
          >
            Open Radar Dashboard
            <ArrowRight size={16} />
          </Link>
          <Link
            href='/platform/account'
            className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-sm border border-border text-foreground hover:bg-muted transition-colors'
          >
            View Account
          </Link>
        </div>

        <p className='text-xs text-muted-foreground'>
          Free standalone tools reset tomorrow. The dashboard runs anytime.
        </p>
      </div>
    </div>
  )
}

function UpsellGate({ limit }: { limit: number }) {
  return (
    <div className='bg-muted/50 rounded-2xl border border-line-strong p-8 md:p-12 text-center'>
      <div className='max-w-lg mx-auto space-y-6'>
        <div
          className='w-16 h-16 rounded-2xl flex items-center justify-center mx-auto'
          style={{ backgroundColor: '#008dcc15' }}
        >
          <Lock size={28} style={{ color: '#008dcc' }} />
        </div>

        <div className='space-y-2'>
          <h3 className='text-xl font-semibold text-foreground'>
            You&apos;ve used all {limit} free audits
          </h3>
          <p className='text-muted-foreground text-sm'>
            Unlock unlimited runs across all 13 tools with Radar, our AI
            visibility platform.
          </p>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 text-left'>
          {[
            {
              icon: Zap,
              text: 'All 13 tools, unlimited runs',
              color: '#FDC304',
            },
            {
              icon: BarChart3,
              text: 'Run all tools at once on any domain',
              color: '#008dcc',
            },
            {
              icon: Bot,
              text: 'Cross-tool insights and action items',
              color: '#8b5cf6',
            },
            { icon: Sparkles, text: 'Built-in AI advisor', color: '#F90B8A' },
          ].map(({ icon: Icon, text, color }) => (
            <div
              key={text}
              className='flex items-center gap-3 bg-background border border-line-strong rounded-xl px-4 py-3'
            >
              <Icon size={18} style={{ color, flexShrink: 0 }} />
              <span className='text-sm text-foreground/80'>{text}</span>
            </div>
          ))}
        </div>

        <div className='flex flex-col sm:flex-row gap-3 justify-center pt-2'>
          <Link
            href='/platform'
            className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-sm transition-colors'
            style={{ backgroundColor: '#008dcc', color: '#fff' }}
          >
            Get Radar Access
            <ArrowRight size={16} />
          </Link>
          <Link
            href='/platform#pricing'
            className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-sm border border-border text-foreground hover:bg-muted transition-colors'
          >
            See Pricing
          </Link>
        </div>

        <p className='text-xs text-muted-foreground'>
          First audit free. Audit packs from $5.
        </p>
      </div>
    </div>
  )
}

/**
 * Subtle usage counter shown below results (runs 1-3).
 * Only shows after first use (not before any use).
 */
export function UsageCounter({
  used,
  limit,
  isTokenHolder,
}: {
  used: number
  limit: number
  isTokenHolder: boolean
}) {
  if (isTokenHolder || used === 0) return null

  const remaining = Math.max(0, limit - used)

  if (remaining === 0) return null

  return (
    <div className='flex items-center justify-center gap-2 py-3'>
      <div className='flex gap-1'>
        {Array.from({ length: limit }).map((_, i) => (
          <div
            key={i}
            className='w-2 h-2 rounded-full transition-colors'
            style={{
              backgroundColor: i < used ? '#008dcc' : 'var(--border)',
            }}
          />
        ))}
      </div>
      <span className='text-xs text-muted-foreground'>
        {remaining} free {remaining === 1 ? 'audit' : 'audits'} remaining
      </span>
    </div>
  )
}
